import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { WorldMap } from '../components/WorldMap';
import { DoodleEditor } from '../components/DoodleEditor';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/api';

const GameContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: #2c3e50;
  color: white;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const PointsDisplay = styled.div`
  background: #27ae60;
  padding: 5px 10px;
  border-radius: 15px;
  font-weight: bold;
`;

const StreakDisplay = styled.div`
  background: #f39c12;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 14px;
`;

const Button = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2980b9;
  }
`;

const RewardButton = styled(Button)`
  background: #f39c12;

  &:hover {
    background: #e67e22;
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }
`;

const LogoutButton = styled(Button)`
  background: #e74c3c;

  &:hover {
    background: #c0392b;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

export const GamePage: React.FC = () => {
  const { user, logout } = useAuth();
  const [showDoodleEditor, setShowDoodleEditor] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [canClaimReward, setCanClaimReward] = useState(true);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await userApi.getProfile();
      setUserProfile(response.data);

      // 检查是否可以领取每日奖励（简化版检查）
      setCanClaimReward(true); // 暂时总是可以领取，实际应该检查日期
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  const handleSaveDoodle = async (imageData: string) => {
    try {
      await userApi.createDoodle(imageData);
      setShowDoodleEditor(false);
      await loadUserProfile(); // 刷新用户数据
      alert('标识保存成功！');
    } catch (error) {
      console.error('Failed to save doodle:', error);
      alert('保存失败，请重试');
    }
  };

  const handleClaimDailyReward = async () => {
    try {
      const response = await userApi.claimDailyReward();
      const { reward_points, daily_login_streak, new_total_points } = response.data;

      alert(`每日奖励领取成功！\n获得 ${reward_points} 涂鸦点\n连续登录 ${daily_login_streak} 天\n当前总点数: ${new_total_points}`);
      setCanClaimReward(false);
      await loadUserProfile(); // 刷新用户数据
    } catch (error: any) {
      console.error('Failed to claim daily reward:', error);
      alert(error.response?.data?.message || '领取失败，请重试');
    }
  };

  if (!user) {
    return null;
  }

  const displayUser = userProfile || user;

  return (
    <GameContainer>
      <Header>
        <h1>全球涂鸦占领</h1>
        <UserInfo>
          {displayUser.avatar_url && (
            <UserAvatar src={displayUser.avatar_url} alt={displayUser.username} />
          )}
          <div>欢迎, {displayUser.username}!</div>
          <PointsDisplay>涂鸦点: {displayUser.doodle_points || displayUser.doodlePoints}</PointsDisplay>
          {userProfile && (
            <StreakDisplay>连续登录: {userProfile.daily_login_streak}天</StreakDisplay>
          )}
          <RewardButton
            onClick={handleClaimDailyReward}
            disabled={!canClaimReward}
          >
            {canClaimReward ? '领取每日奖励' : '今日已领取'}
          </RewardButton>
          <Button onClick={() => setShowDoodleEditor(true)}>
            编辑标识
          </Button>
          <LogoutButton onClick={logout}>
            退出
          </LogoutButton>
        </UserInfo>
      </Header>

      <WorldMap />

      <Modal isOpen={showDoodleEditor}>
        <ModalContent>
          <CloseButton onClick={() => setShowDoodleEditor(false)}>
            ×
          </CloseButton>
          <DoodleEditor
            onSave={handleSaveDoodle}
            initialIcon={displayUser.doodle_icon || displayUser.doodleIcon}
          />
        </ModalContent>
      </Modal>
    </GameContainer>
  );
};